import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { format } from "date-fns";
import { de, enUS } from 'date-fns/locale';
import { useTranslation } from "react-i18next";
import { Calendar, Clock, User, ArrowRight, Mail, AlertTriangle, XCircle, Package } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { getTenantTimeZone, toTimeZoneWallClockDate } from "@sylva/shared-types";

interface ServiceChange {
  type: 'added' | 'removed' | 'unchanged';
  serviceName: string;
  serviceColor: string | null;
}

interface TeamMemberChange {
  type: 'changed' | 'unchanged';
  oldTeamMemberName?: string;
  newTeamMemberName?: string;
}

interface Conflict {
  type: 'team_member' | 'resource_capacity' | 'appointment_type_resource' | 'blocked_period';
  severity: 'error' | 'warning';
  message: string;
  details: {
    resourceName?: string;
    teamMemberName?: string;
    requiredResource?: string;
  };
}

interface Warning {
  type: 'capacity_approaching' | 'back_to_back_appointments';
  message: string;
  details?: Record<string, unknown>;
}

interface AppointmentUpdateConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerName: string;
  customerHasEmail?: boolean;
  oldStartTime: Date;
  oldEndTime: Date;
  newStartTime: Date;
  newEndTime: Date;
  timeZone?: string;
  serviceChanges: ServiceChange[];
  teamMemberChanges?: TeamMemberChange[];
  conflicts?: Conflict[];
  warnings?: Warning[];
  onConfirm: (sendNotification: boolean) => void;
  onCancel?: () => void;
}

export const AppointmentUpdateConfirmDialog = ({
  open,
  onOpenChange,
  customerName,
  customerHasEmail = true,
  oldStartTime,
  oldEndTime,
  newStartTime,
  newEndTime,
  timeZone,
  serviceChanges,
  teamMemberChanges = [],
  conflicts = [],
  warnings = [],
  onConfirm,
  onCancel,
}: AppointmentUpdateConfirmDialogProps) => {
  const { t, i18n } = useTranslation();
  const [sendNotification, setSendNotification] = useState(false);
  const resolvedTimeZone = getTenantTimeZone(timeZone);

  // Get the appropriate locale for date-fns formatting
  const locale = i18n.language === 'de' ? de : enUS;
  const oldStartDisplay = toTimeZoneWallClockDate(oldStartTime, resolvedTimeZone);
  const oldEndDisplay = toTimeZoneWallClockDate(oldEndTime, resolvedTimeZone);
  const newStartDisplay = toTimeZoneWallClockDate(newStartTime, resolvedTimeZone);
  const newEndDisplay = toTimeZoneWallClockDate(newEndTime, resolvedTimeZone);

  // Format dates using the preferred pattern from the application
  const formatDate = (date: Date) => {
    return format(date, t('dateFormatShort'), { locale });
  };

  // Check if there are any blocking conflicts
  const hasErrors = conflicts.some(c => c.severity === 'error');
  const errorConflicts = conflicts.filter(c => c.severity === 'error');
  const warningConflicts = conflicts.filter(c => c.severity === 'warning');

  // Check if time has changed
  const timeChanged = oldStartTime.getTime() !== newStartTime.getTime() ||
                      oldEndTime.getTime() !== newEndTime.getTime();

  // Check if services have changed
  const servicesChanged = serviceChanges.some(sc => sc.type !== 'unchanged');

  // Check if team members have changed
  const teamMembersChanged = teamMemberChanges.some(tm => tm.type === 'changed');

  const handleConfirm = () => {
    onConfirm(sendNotification);
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  const handleOpenChange = (open: boolean) => {
    // If closing the dialog (open = false), treat it as a cancel
    if (!open && onCancel) {
      onCancel();
    }
    onOpenChange(open);
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent className="sm:max-w-[600px]">
        <AlertDialogHeader className="space-y-4">
          <AlertDialogTitle className="flex items-center gap-3 text-xl">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10">
              <Calendar className="h-5 w-5 text-primary" />
            </div>
            {t('confirmAppointmentUpdate')}
          </AlertDialogTitle>

          <AlertDialogDescription asChild>
            <div className="space-y-6">
              {/* Conflicts Section */}
              {errorConflicts.length > 0 && (
                <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-red-100 flex-shrink-0 mt-0.5">
                    <XCircle className="h-3.5 w-3.5 text-red-600" />
                  </div>
                  <div className="space-y-2">
                    <div className="text-sm font-medium text-red-800">
                      {t('schedulingConflicts')}
                    </div>
                    {errorConflicts.map((conflict, index) => (
                      <div key={index} className="text-sm text-red-700">
                        • {conflict.message}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Warnings Section */}
              {(warningConflicts.length > 0 || warnings.length > 0) && (
                <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-amber-100 flex-shrink-0 mt-0.5">
                    <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
                  </div>
                  <div className="space-y-2">
                    <div className="text-sm font-medium text-amber-800">
                      {t('schedulingWarnings')}
                    </div>
                    {warningConflicts.map((conflict, index) => (
                      <div key={`conflict-${index}`} className="text-sm text-amber-700">
                        {conflict.message}
                      </div>
                    ))}
                    {warnings.map((warning, index) => (
                      <div key={`warning-${index}`} className="text-sm text-amber-700">
                        {warning.message}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Notification Toggle */}
              {!hasErrors && (
                <div className={`flex items-start gap-3 p-4 rounded-lg border ${
                  !customerHasEmail
                    ? 'bg-slate-50 border-slate-200'
                    : sendNotification
                      ? 'bg-amber-50 border-amber-200'
                      : 'bg-green-50 border-green-200'
                }`}>
                  <div className={`flex items-center justify-center w-6 h-6 rounded-full flex-shrink-0 mt-0.5 ${
                    !customerHasEmail
                      ? 'bg-slate-100'
                      : sendNotification
                        ? 'bg-amber-100'
                        : 'bg-green-100'
                  }`}>
                    <Mail className={`h-3.5 w-3.5 ${
                      !customerHasEmail
                        ? 'text-slate-400'
                        : sendNotification
                          ? 'text-amber-600'
                          : 'text-green-600'
                    }`} />
                  </div>
                  <div className="flex-1 space-y-3">
                    <div className={`text-sm ${
                      !customerHasEmail
                        ? 'text-slate-600'
                        : sendNotification
                          ? 'text-amber-800'
                          : 'text-green-800'
                    }`}>
                      {!customerHasEmail
                        ? t('updateNotificationDisabled')
                        : sendNotification
                          ? t('updateNotificationEnabled')
                          : t('updateNotificationDisabled')}
                    </div>
                    <div className="flex items-center gap-3">
                      <Switch
                        checked={sendNotification}
                        onCheckedChange={setSendNotification}
                        disabled={!customerHasEmail}
                      />
                      <span className={`text-sm font-medium ${
                        !customerHasEmail
                          ? 'text-slate-500'
                          : sendNotification
                            ? 'text-amber-800'
                            : 'text-green-800'
                      }`}>
                        {!customerHasEmail
                          ? t('customerHasNoEmail')
                          : sendNotification
                            ? t('notificationEnabled')
                            : t('notificationDisabled')}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Changes Summary */}
              <div className="bg-gradient-to-r from-slate-50 to-slate-100 p-5 rounded-xl border border-slate-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-slate-200">
                    <User className="h-4 w-4 text-slate-600" />
                  </div>
                  <div className="font-semibold text-slate-900">
                    {customerName}
                  </div>
                </div>

                <div className="space-y-4">
                  {/* Time Comparison */}
                  {timeChanged && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Clock className="h-4 w-4 text-slate-500" />
                        <div className="text-sm font-medium text-slate-600">
                          {t('appointmentTime')}
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        {/* Old Time */}
                        <div className="flex-1 bg-card p-3 rounded-lg border border-slate-300">
                            <div className="text-xs text-slate-500 mb-1">{t('currentTime')}</div>
                            <div className="text-sm font-medium text-slate-800">
                              {formatDate(oldStartDisplay)}
                            </div>
                            <div className="text-base font-semibold text-slate-900">
                              {format(oldStartDisplay, t('timeFormatHourMinute'), { locale })} - {format(oldEndDisplay, t('timeFormatHourMinute'), { locale })}
                            </div>
                          </div>

                        {/* Arrow */}
                        <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 flex-shrink-0">
                          <ArrowRight className="h-3 w-3 text-primary" />
                        </div>

                        {/* New Time */}
                        <div className="flex-1 bg-primary/5 p-3 rounded-lg border border-primary/20">
                            <div className="text-xs text-primary/70 mb-1">{t('newTime')}</div>
                            <div className="text-sm font-medium text-primary/90">
                              {formatDate(newStartDisplay)}
                            </div>
                            <div className="text-base font-semibold text-primary">
                              {format(newStartDisplay, t('timeFormatHourMinute'), { locale })} - {format(newEndDisplay, t('timeFormatHourMinute'), { locale })}
                            </div>
                          </div>
                      </div>
                    </div>
                  )}

                  {/* Service Changes */}
                  {servicesChanged && (
                    <div className="pt-4 border-t border-slate-300">
                      <div className="flex items-center gap-2 mb-3">
                        <Package className="h-4 w-4 text-slate-500" />
                        <div className="text-sm font-medium text-slate-600">
                          {t('serviceChanges')}
                        </div>
                      </div>
                      <div className="space-y-2">
                        {serviceChanges.map((change, index) => (
                          <div key={index} className="flex items-center gap-2">
                            {change.type === 'added' && (
                              <>
                                <span className="text-xs text-green-700 font-medium">+ {t('added')}:</span>
                                <Badge
                                  variant="secondary"
                                  className="flex items-center gap-2 bg-green-50 border-green-200"
                                >
                                  <div
                                    className="w-3 h-3 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: change.serviceColor || '#3b82f6' }}
                                  />
                                  {change.serviceName}
                                </Badge>
                              </>
                            )}
                            {change.type === 'removed' && (
                              <>
                                <span className="text-xs text-red-700 font-medium">- {t('removed')}:</span>
                                <Badge
                                  variant="secondary"
                                  className="flex items-center gap-2 bg-red-50 border-red-200 line-through opacity-75"
                                >
                                  <div
                                    className="w-3 h-3 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: change.serviceColor || '#3b82f6' }}
                                  />
                                  {change.serviceName}
                                </Badge>
                              </>
                            )}
                            {change.type === 'unchanged' && (
                              <Badge
                                variant="secondary"
                                className="flex items-center gap-2"
                              >
                                <div
                                  className="w-3 h-3 rounded-full flex-shrink-0"
                                  style={{ backgroundColor: change.serviceColor || '#3b82f6' }}
                                />
                                {change.serviceName}
                              </Badge>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Team Member Changes */}
                  {teamMembersChanged && (
                    <div className="pt-4 border-t border-slate-300">
                      <div className="flex items-center gap-2 mb-3">
                        <User className="h-4 w-4 text-slate-500" />
                        <div className="text-sm font-medium text-slate-600">
                          {t('teamMemberChanges')}
                        </div>
                      </div>
                      <div className="space-y-2">
                        {teamMemberChanges
                          .filter(tm => tm.type === 'changed')
                          .map((change, index) => (
                            <div key={index} className="flex items-center gap-2 text-sm">
                              <span className="text-slate-700">
                                {change.oldTeamMemberName || t('unassigned')}
                              </span>
                              <ArrowRight className="h-3 w-3 text-slate-400" />
                              <span className="text-primary font-medium">
                                {change.newTeamMemberName || t('unassigned')}
                              </span>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* No changes message */}
                  {!timeChanged && !servicesChanged && !teamMembersChanged && (
                    <div className="text-sm text-slate-500 text-center py-2">
                      {t('noChangesDetected')}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter className="gap-3 pt-6">
          <AlertDialogCancel
            onClick={handleCancel}
            className="px-6"
          >
            {t('cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={hasErrors}
            className="px-6"
          >
            <Calendar className="h-4 w-4 mr-2" />
            {hasErrors ? t('cannotUpdate') : t('confirmUpdate')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
